export type WithStringifiedKeys<T, K extends keyof T> = Omit<T, K> & {
  [Key in K]-?: string;
};

export type OptionalKeysArrays<T> = {
  string?: readonly (keyof T)[];
  number?: readonly (keyof T)[];
};

export const normalizeFormDefaults = <
  T extends Record<string, unknown>,
  K extends readonly (keyof T)[],
  E extends Partial<Record<keyof T, unknown>> = T,
>(
  defaults: T,
  entry: E | undefined,
  keys: K,
) => normalizeOptionalFields({ ...defaults, ...(entry || {}) }, keys);

export const normalizeOptionalFields = <
  T extends Record<string, unknown>,
  K extends readonly (keyof T)[],
>(
  values: T,
  keys: K,
) => {
  const next = { ...values } as T & Record<K[number], string>;
  for (const key of keys) {
    const value = values[key];
    (next as Record<string, unknown>)[key as string] =
      value === undefined || value === null ? '' : String(value);
  }
  return next;
};

export const denormalizeOptionalStringFields = <
  T extends Record<string, unknown>,
  K extends readonly (keyof T)[],
>(
  values: T,
  keys: K,
) => {
  const next = { ...values } as T & Record<K[number], string | undefined>;
  for (const key of keys) {
    const value = values[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      (next as Record<string, unknown>)[key as string] =
        trimmed === '' ? undefined : trimmed;
    }
  }
  return next;
};

export const denormalizeOptionalNumberStringFields = <
  T extends Record<string, unknown>,
  K extends readonly (keyof T)[],
>(
  values: T,
  keys: K,
) => {
  const next = { ...values } as T & Record<K[number], number | undefined>;
  for (const key of keys) {
    const value = values[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const parsed = trimmed === '' ? undefined : Number(trimmed);
      (next as Record<string, unknown>)[key as string] = Number.isFinite(parsed)
        ? parsed
        : undefined;
    }
  }
  return next;
};

export const denormalizeOptionalFields = <T extends Record<string, unknown>>(
  values: T,
  optionalKeys: OptionalKeysArrays<T>,
) =>
  denormalizeOptionalNumberStringFields(
    denormalizeOptionalStringFields(values, optionalKeys.string || []),
    optionalKeys.number || [],
  );
